<template>
  <div id="content">
    <preview-popup
        :preview-data="previewData"
        :show-preview="preview"
        @hide-preview="hidePreview"
    />
    <form>
      <div class="inner">
        <p class="paging">
          <router-link
              :to="{ name: moduleName }"
              class="btn btn_back"
              v-translate>Seznam
          </router-link>
        </p>
        <p class="buttons">
          <a class="btn btn_action btn_grey btn_preview"
             :title="'Náhled' | translate"
             @click.stop="showPreview"
             v-translate>Náhled</a>
        </p>
        <h1 v-translate>Nové oznámení</h1>
      </div>
      <div class="responsiveTabs">
        <div class="tabs_content">
          <section>
            <div class="left">
              <h2 v-translate>Nastavení oznámení</h2>
            </div>
            <div class="right">
              <div class="field_left">
                <text-input id="messageName"
                            v-model="entity.name"
                            :required="true"
                            ref="messagenameref"
                >
                  <translate class="translate">Název oznámení</translate>
                </text-input>
              </div>
              <div class="field_right">
                <p class="field">
                  <select-box
                      id="messageType"
                      :required="true"
                      v-model="messageTypeComputed"
                      :options="messageTypeOptions"
                      ref="messagetyperef"
                  >
                    <translate class="translate">Typ oznámení</translate>
                  </select-box>
                </p>
              </div>
              <div class="field checkboxes">
                <p class="field">
                  <filter-checkbox
                      id="messageForceConfirmation"
                      name="messageForceConfirmation"
                      :val="entity.force_confirmation"
                      v-model="entity.force_confirmation"
                  >
                    <translate class="translate">Vynucení potvrzení</translate>
                  </filter-checkbox>
                </p>
              </div>

              <div v-if="!entity.force_confirmation" class="field_left">
                <date-picker edit-time
                             id="templateTimeValidity"
                             date-format="d. m. yy"
                             attrKey="time_validity"
                             v-model="entity.time_validity"
                             :required="false"
                             @update-date="updateTimeValidityDate"
                >
                  <translate key="templateTimeValidityTitle" class="translate">Označit za přečtené v</translate>
                </date-picker>
              </div>

              <div v-if="entity.force_confirmation" class="field_left">
                <p class="field">
                  <text-input id="messageTimer"
                              v-model="entity.timer"
                              :required="false"
                              ref="messagetimerref"
                              :help-text="$gettext('Počet vteřin zobrazení oznámení před možností potvrdit přečtení.')"
                              type="number">
                    <translate key="messageTimerTitle" class="translate">Časovač</translate>
                  </text-input>
                </p>
              </div>
            </div>
          </section>

          <section>
            <div class="left">
              <translation-language-select
                  :languages="languagesToSelect"
                  :existingLanguages="existingLanguages"
                  :selectedLanguage="selectedLanguageId"
                  :shouldLoadDefaultLanguage="!this.$route.params.id_template && !this.$route.params.forward_message"
                  @selectLanguage="onSelectLanguage"
                  @languagesSelectionChanged="onLanguagesSelectionChanged"
                  ref="translationlanguageref"
              />
            </div>

            <div class="right">

              <div class="field">
                <text-input id="translationSubject"
                            v-model="translation.subject"
                            :required="true"
                            name="translationSubject"
                            ref="translationsubjectref"
                            :disabled="translationFieldsDisabled"
                            maxLength="50"
                >
                  <translate class="translate">Nadpis oznámení</translate>
                </text-input>
              </div>

              <p class="field">
                <text-input
                    maxLength="255"
                    id="translationShortBody"
                    name="translationShortBody"
                    v-model="translation.short_body"
                    :required="true"
                    ref="translationshortbodyref"
                    :disabled="translationFieldsDisabled"
                >
                  <translate class="translate">Zkrácená verze oznámení</translate>
                </text-input>
              </p>

              <p class="field">
                <html-editor
                    id="translationBody"
                    name="translationBody"
                    :custom-toolbar="customToolbar"
                    :body="translation.body"
                    :required="true"
                    ref="translationbodyref"
                    @newVal="translation.body = arguments[0]"
                    :disabled="translationFieldsDisabled">
                  <translate class="translate">Tělo oznámení</translate>
                </html-editor>
              </p>
            </div>
          </section>

          <section>
            <div class="left">
              <h2 v-translate>Výběr příjemců</h2>
              <p>
                <translate class="translate">Počet příjemců:</translate>
                {{ numberOfRecipients }} <br/>
                <a
                    v-if="recipientsCount > 0 && !sendToAll"
                    :title="'Zobrazit příjemce' | translate"
                    @click.stop.prevent="initRecipientPopup"
                    v-translate>Zobrazit příjemce
                </a>
              </p>
            </div>

            <div class="right">

              <div class="field_left">
                <filter-checkbox
                    id="messageSendToAll"
                    name="messageSendToAll"
                    :val="sendToAll"
                    :pClass="'field send_all'"
                    v-model="sendToAll"
                    @input="onRecipientsChange"
                >
                  <translate class="translate">Odeslat všem</translate>
                </filter-checkbox>
              </div>

              <div class="field">
                <multiselect-searchbox
                    v-if="$_loggedUser_action_allowed('subscribers')"
                    :searchFunction="searchSubscribers"
                    v-model="entity.recipients.id_subscribers"
                    :show-id="true"
                    :disabled="sendToAll"
                    @input="onRecipientsChange"
                >{{ $gettext('Odběratel') }}
                </multiselect-searchbox>
                <div class="field_left">
                  <filter-checkbox
                      id="combineConditions"
                      name="combineConditions"
                      :val="combineConditions"
                      v-model="combineConditions"
                      :disabled="sendToAll"
                      :pClass="'field send_all'"
                      @input="onRecipientsChange"
                  >
                    <translate class="translate">Kombinovat následující podmínky</translate>
                  </filter-checkbox>
                </div>
                <multiselect-searchbox
                    v-if="$_loggedUser_action_allowed('brands')"
                    :searchFunction="searchBrands"
                    v-model="entity.recipients.id_brands"
                    :show-id="true"
                    :disabled="sendToAll"
                    @input="onRecipientsChange"
                >{{ $gettext('Brand') }}
                </multiselect-searchbox>
                <multiselect-searchbox
                    v-if="$_loggedUser_canChangeOperator && !$_loggedUser_selectedOperatorId"
                    :searchFunction="searchOperators"
                    v-model="entity.recipients.id_operators"
                    :show-id="true"
                    :disabled="sendToAll"
                    @input="onRecipientsChange"
                >{{ $gettext('Operátor') }}
                </multiselect-searchbox>
                <multiselect-searchbox
                    v-if="$_loggedUser_action_allowed('services')"
                    :searchFunction="searchServices"
                    v-model="entity.recipients.id_services"
                    :show-id="true"
                    :disabled="sendToAll"
                    @input="onRecipientsChange"
                >{{ $gettext('Služba') }}
                </multiselect-searchbox>
                <multiselect-searchbox
                    v-if="$_loggedUser_action_allowed('packages')"
                    :searchFunction="searchPackages"
                    v-model="entity.recipients.id_channel_packages"
                    :show-id="true"
                    :disabled="sendToAll"
                    @input="onRecipientsChange"
                >{{ $gettext('Balíček kanálů') }}
                </multiselect-searchbox>
                <multiselect-searchbox
                    v-if="$_loggedUser_action_allowed('deviceTypes')"
                    :searchFunction="searchDeviceTypes"
                    v-model="entity.recipients.id_device_types"
                    :show-id="true"
                    :disabled="sendToAll"
                    @input="onRecipientsChange"
                >{{ $gettext('Typ zařízení') }}
                </multiselect-searchbox>
                <div class="field_left" v-if="$_loggedUser_action_allowed('subscribers')">
                  <select-box id="subscriberType"
                              v-model="entity.recipients.subscriber_type"
                              :disabled="sendToAll"
                              :options="subscriberTypes"
                              ref="subscribertyperef"
                              @input="onRecipientsChange"
                  >
                    <translate class="translate">Typ odběratele</translate>
                  </select-box>
                  <p></p>
                </div>
                <multiselect-searchbox
                    v-if="$_loggedUser_action_allowed('tags')"
                    :searchFunction="searchTags"
                    v-model="entity.recipients.id_subscriber_tags"
                    :show-id="true"
                    :disabled="sendToAll"
                    @input="onRecipientsChange"
                >{{ $gettext('Štítky odběratele') }}
                </multiselect-searchbox>
              </div>
            </div>
          </section>

          <section class="buttons">
            <p>
              <router-link :to="{ name: moduleName }" class="btn btn_back btn_seznam" v-translate>Seznam</router-link>
              <input type="submit"
                     :class="['btn']"
                     :value="'Odeslat oznámení' | translate"
                     @click.prevent.stop="submitForm()">
            </p>
          </section>
        </div>
      </div>
    </form>
    <new-message-recipients-popup
        v-if="popupRecipientsData"
        :combine-recipient-conditions-request="combineConditions"
        :recipients-request="recipientsRequest"
        @close-popup="closeRecipientPopup">
    </new-message-recipients-popup>
    <new-message-save-as-template-popup
        v-if="popupSaveAsTemplateData"
        :message-params="messageParams"
        @close-popup="closeSaveAsTemplatePopup"
        @set-loading-message="setLoadingMessage"
        @empty-message="emptyMessage"
        @set-error-message="setErrorMessage">
    </new-message-save-as-template-popup>

  </div>
</template>

<script>
import messages from "./mixins/messages"
import types from "./mixins/types"
import detailActions from "@/components/mixins/detail-actions"
import TranslationLanguageSelect from "../messagetemplates/languanges/TranslationLanguageSelect"
import NewMessageRecipientsPopup from "./popup/NewMessageRecipientsPopup"
import HtmlEditor from "../../common/HtmlEditor"
import NewMessageSaveAsTemplatePopup from "./popup/NewMessageSaveAsTemplatePopup"
import PreviewPopup from "./popup/PreviewPopup.vue";

export default {
  name: "Message",
  components: {
    PreviewPopup,
    NewMessageSaveAsTemplatePopup,
    HtmlEditor, NewMessageRecipientsPopup, TranslationLanguageSelect
  },
  mixins: [detailActions, messages, types],
  data() {
    return {
      preview: false,
      customToolbar: [
        ["bold", "italic", "underline"],
        [{
          color: []
        }]
      ]
    }
  },
  created: function (MessageService, OperatorService, SubscriberService,
                     ServiceService, PackageService, DeviceService,
                     MessageTemplateService, BrandService, TagService) {
    this.entityService = MessageService
    this.operatorService = OperatorService
    this.serviceService = ServiceService
    this.packageService = PackageService
    this.subscriberService = SubscriberService
    this.deviceService = DeviceService
    this.messageTemplateService = MessageTemplateService
    this.brandService = BrandService
    this.tagService = TagService
    this.subscriberService.getSubscriberTypeList({'id_operator': null})
        .then(data => {
          for (let key in data.subscriber_types) {
            data.subscriber_types[key].id = data.subscriber_types[key].text
          }
          this.subscriberTypes = [{id: null, text: this.$gettext('Není vybrán')},]
          this.subscriberTypes = this.subscriberTypes.concat(data.subscriber_types)
        })

    if (this.$route.params.id_template) {
      this.setLoadingMessage()
      this.messageTemplateService.getEntity(this.$route.params.id_template, {id_selected_operator: this.$_loggedUser_selectedOperatorId}).then(data => {
        this.emptyMessage()
        let template = data['message_template']
        this.entity.name = template.name
        this.entity.type = template.type
        this.entity.timer = template.timer
        this.entity.force_confirmation = template.force_confirmation
        this.entity.time_validity = template.time_validity
        this.entity.translations = this.retypeTranslations(template.translations);
        if (this.entity.translations) {
          this.onSelectLanguage(Object.keys(this.entity.translations)[0]);
        }
      })
    }
  },
  mounted() {
    this.getDefaults();
  },
  methods: {
    initRecipientPopup() {
      this.popupRecipientsData = 1
    },
    closeRecipientPopup() {
      this.popupRecipientsData = null
    },
    initSaveAsTemplatePopup() {
      this.popupSaveAsTemplateData = 1
    },
    closeSaveAsTemplatePopup() {
      this.popupSaveAsTemplateData = null
      this.finishRedirect(this.finishRedirectRedirect, this.finishRedirectData)
    },
    searchBrands(search, selectAll = false) {
      return this.brandService.searchBrandsForRecipients({search: search, select_all: selectAll});
    },
    searchOperators(search, selectAll = false) {
      return this.operatorService.searchOperatorsForRecipients({search: search, select_all: selectAll});
    },
    searchDeviceTypes(search, selectAll = false) {
      return this.deviceService.searchDeviceTypesForRecipients({search: search, select_all: selectAll});
    },
    searchPackages(search, selectAll = false) {
      return this.packageService.searchPackagesByNameOrId({
        search: search,
        selected_operator: this.$_loggedUser_selectedOperatorId,
        select_all: selectAll
      });
    },
    searchServices(search, selectAll = false) {
      return this.serviceService.searchServicesByNameOrId({
        search: search,
        id_selected_operator: this.$_loggedUser_selectedOperatorId,
        select_all: selectAll
      });
    },
    searchSubscribers(search, selectAll = false) {
      return this.subscriberService.searchSubscribers({
        search: search,
        selected_operator: this.$_loggedUser_selectedOperatorId,
        select_all: selectAll
      });
    },
    searchTags(search, selectAll = false) {
      return this.tagService.searchTagByNameAndOperatorId(this.$_loggedUser_selectedOperatorId ? this.$_loggedUser_selectedOperatorId : 0, {
        name_search: search,
        select_all: selectAll
      });
    },
    getSubscriberTypeList(idOperator) {
      return this.entityService.getSubscriberTypeList({'id_operator': idOperator})
          .then(data => {
            this.subscriberTypes = data.subscriber_types
          })
    },
    submitForm(redirect = false) {
      if (this.recipientsCount < 1 && !this.sendToAll) {
        this.setMessage(this.$gettext('Musí být vybrán alespoň jeden příjemce.'), 'error')
        return
      }

      this.saveCurrentTranslation()
      this.createEntity(redirect)
    },
    createEntity(redirect = false) {
      let preProcessedParams = this._beforeCreateEntityActions({...this.entity})
      let params = this.filterFormParams(preProcessedParams)
      this.entityService.createEntity(params)
          .then(data => {
            this.saveLastVisited(data.id)
            if (this.$route.params.id_template) {
              this.finishRedirect(redirect, data)
            } else {
              this.finishRedirectRedirect = redirect
              this.finishRedirectData = data
              this.askForTemplateSaving(params)
            }
          })
          .catch(function (error) {
            this.setErrorMessage(this._processDetailErrorMessage(error))
          }.bind(this))
    },
    askForTemplateSaving(params) {
      this.messageParams = params
      this.initSaveAsTemplatePopup()
    },
    finishRedirect(redirect = false, data) {
      if (redirect === true) {
        this.redirect(this.moduleName)
      } else {
        this.redirect(this.detailPageRouteName, {id: data.id})
      }
    },
    onLanguagesSelectionChanged(newLanguagesSelection) {
      let translations = {}
      let foundCurrentlySelectedLanguage = false
      let firstLanguageId = null

      for (let key in newLanguagesSelection) {
        const lang = newLanguagesSelection[key].value

        if (firstLanguageId === null) {
          firstLanguageId = lang
        }

        if (lang === this.selectedLanguageId) {
          foundCurrentlySelectedLanguage = true
        }

        if (this.entity.translations.hasOwnProperty(lang)) {
          translations[lang] = this.entity.translations[lang]
        } else {
          translations[lang] = {body: "", subject: "", short_body: ""}
        }

      }

      if (!foundCurrentlySelectedLanguage) {
        this.onSelectLanguage(firstLanguageId)
      }

      this.entity.translations = translations
    },
    async onSelectLanguage(idLanguage) {
      this.saveCurrentTranslation()

      this.selectedLanguageId = idLanguage
      let tempTranslation = this.entity.translations[idLanguage]
      if (idLanguage !== null && tempTranslation !== null && tempTranslation) {
        this.translation.language = idLanguage
        this.translation.subject = tempTranslation.subject
        this.translation.short_body = tempTranslation.short_body
        this.translation.body = tempTranslation.body
      } else {
        this.translation.language = null
        this.translation.subject = ""
        this.translation.short_body = ""
        this.translation.body = ""
      }
    },
    saveCurrentTranslation() {
      if (this.selectedLanguageId !== null) {
        this.entity.translations[this.selectedLanguageId] = {
          "subject": this.translation.subject,
          "short_body": this.translation.short_body,
          "body": this.translation.body
        }
      }
    },
    updateTimeValidityDate(dateInfo) {
      this.entity.time_validity = dateInfo.date
    },
    onRecipientsChange() {
      this.entityService.messageRecipientsCount(
          {
            id_selected_operator: this.$_loggedUser_selectedOperatorId,
            recipients: this.entity.recipients,
            combine_recipients_conditions: this.combineConditions
          })
          .then((data) => {
            this.recipientsCount = data['totalCount']
          })
    },
    showPreview() {
      this.preview = true
    },
    hidePreview() {
      this.preview = false
    }
  },
  computed: {
    messageTypeComputed: {
      get() {
        return this.entity.type
      },
      set(newValue) {
        this.entity.type = newValue
      }
    },
    languagesToSelect() {
      let languages = {}
      let languagesToSelect = []
      Object.assign(languages, this.$language.available)
      for (let key in languages) {
        languagesToSelect.push({value: key.toLowerCase(), text: key})
      }
      return languagesToSelect
    },
    existingLanguages() {
      let existingLanguages = []
      for (let key in this.entity.translations) {
        existingLanguages.push({value: key.toLowerCase(), text: key})
      }
      return existingLanguages
    },
    translationFieldsDisabled: {
      get() {
        return this.selectedLanguageId === null
      }
    },
    numberOfRecipients() {
      if (this.sendToAll) {
        return this.$gettext('Všichni')
      } else {
        return this.recipientsCount;
      }
    },
    recipientsRequest() {
      return this.entity.recipients;
    },
    previewData() {
      return {
        subject: this.translation.subject,
        short_body: this.translation.short_body,
        body: this.translation.body,
        type: this.entity.type
      }
    }
  },
  watch: {
    entity: {
      handler(newVal) {
        if (!newVal.force_confirmation) {
          this.entity.timer = null
        } else {
          this.entity.time_validity = null
        }
      }, deep: true
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/views/messages/MessageNew.vue